// Centralised date-format helpers. User preference ('MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'MMM D, YYYY')
// is stored on the user profile and read from localStorage as a client-side cache.

export const DATE_FORMATS = [
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (EU)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
  { value: "MMM D, YYYY", label: "MMM D, YYYY (long)" },
];

export const DEFAULT_DATE_FORMAT = "MMM D, YYYY";
const LS_KEY = "pg_date_format";

export function getDateFormat() {
  try {
    return localStorage.getItem(LS_KEY) || DEFAULT_DATE_FORMAT;
  } catch {
    return DEFAULT_DATE_FORMAT;
  }
}

export function setDateFormat(fmt) {
  try {
    localStorage.setItem(LS_KEY, fmt || DEFAULT_DATE_FORMAT);
    // Fire an event so open screens can re-render without a reload
    window.dispatchEvent(new CustomEvent("pg-date-format-change", { detail: fmt }));
  } catch {
    /* no-op */
  }
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Accepts YYYY-MM-DD or anything Date can parse; returns "" if invalid.
export function formatDate(input, fmt) {
  if (!input) return "";
  const d = parseDateFlexible(input);
  if (!d) return "";
  const chosen = fmt || getDateFormat();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const pad = (n) => String(n).padStart(2, "0");
  switch (chosen) {
    case "MM/DD/YYYY": return `${pad(m)}/${pad(day)}/${y}`;
    case "DD/MM/YYYY": return `${pad(day)}/${pad(m)}/${y}`;
    case "YYYY-MM-DD": return `${y}-${pad(m)}-${pad(day)}`;
    case "MMM D, YYYY":
    default:           return `${MONTHS_SHORT[m - 1]} ${day}, ${y}`;
  }
}

// Accepts multiple user-typed formats. Returns Date or null.
// Supports: YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, M/D/YYYY, MMM D YYYY, etc.
export function parseDateFlexible(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  if (!s) return null;
  // ISO
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return buildDate(+m[1], +m[2], +m[3]);
  // MM/DD/YYYY vs DD/MM/YYYY — disambiguate by user preference when both are plausible.
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (m) {
    const a = +m[1], b = +m[2];
    let y = +m[3]; if (y < 100) y += 2000;
    const pref = getDateFormat();
    // If one number > 12 it must be the day.
    if (a > 12 && b <= 12) return buildDate(y, b, a); // DD/MM
    if (b > 12 && a <= 12) return buildDate(y, a, b); // MM/DD
    if (pref === "DD/MM/YYYY") return buildDate(y, b, a);
    return buildDate(y, a, b); // default to MM/DD
  }
  // Fallback: let Date parse (handles "Feb 4, 2026", "2026-02-04T…", etc.)
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function buildDate(y, m, d) {
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt.getTime())) return null;
  return dt;
}

// Normalise any user-typed value into YYYY-MM-DD for storage. Returns "" if unparseable.
export function toIsoDate(input) {
  const d = parseDateFlexible(input);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
